import React, { useEffect, useRef } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { observeTrainingSection, trackTrainingEvent } from '../trainingAnalytics';
import TrainingMedia from './TrainingMedia';

const ui = {
  fr: {
    audience: 'Ce parcours vous correspond si…',
    capabilities: 'Ce que vous saurez mettre en pratique',
    modules: 'Les six modules du parcours',
    faq: 'Questions fréquentes',
    before: 'Avant',
    after: 'Après le parcours',
    understand: 'Comprendre',
    practice: 'Pratiquer',
    produce: 'Produire',
    intake: 'Rentrée',
    modes: 'Présentiel à Douala ou en ligne',
    duration: 'Durée',
    capacity: 'Capacité pédagogique',
    inPersonCapacity: 'en présentiel',
    onlineCapacity: 'en ligne',
    included: 'Ce qui est inclus',
    heroMedia: 'Placeholder média — environnement de formation CSL BRANDS',
    transformationMedia: 'Placeholder média — mise en pratique avant/après',
    methodMedia: 'Placeholder média — séance de travail accompagnée',
    laboratoryMedia: 'Placeholder média — laboratoire e-commerce',
    certifications: 'Certifications annoncées',
    investmentCta: 'Choisir mon mode',
  },
  en: {
    audience: 'This program is for you if…',
    capabilities: 'What you will learn to put into practice',
    modules: 'The six program modules',
    faq: 'Frequently asked questions',
    before: 'Before',
    after: 'After the program',
    understand: 'Understand',
    practice: 'Practise',
    produce: 'Produce',
    intake: 'Start date',
    modes: 'In person in Douala or online',
    duration: 'Duration',
    capacity: 'Learning capacity',
    inPersonCapacity: 'in person',
    onlineCapacity: 'online',
    included: 'What is included',
    heroMedia: 'Media placeholder — CSL BRANDS training environment',
    transformationMedia: 'Media placeholder — before-and-after practical work',
    methodMedia: 'Media placeholder — guided working session',
    laboratoryMedia: 'Media placeholder — e-commerce laboratory',
    certifications: 'Stated certifications',
    investmentCta: 'Choose my mode',
  },
};

const formatMoney = (amount) => `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} F CFA`;

const formatDuration = ({ value, unit }, locale) => {
  if (unit === 'year') {
    return locale === 'fr'
      ? `${value} an${value > 1 ? 's' : ''}`
      : `${value} year${value > 1 ? 's' : ''}`;
  }
  return locale === 'fr'
    ? `${value} mois`
    : `${value} month${value > 1 ? 's' : ''}`;
};

const formatIntake = (isoDate, locale) => new Intl.DateTimeFormat(
  locale === 'fr' ? 'fr-FR' : 'en-GB',
  { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' },
).format(new Date(`${isoDate}T00:00:00Z`));

const feeText = (label, amount, locale) => (
  `${label}${locale === 'fr' ? ' :' : ':'} ${formatMoney(amount)}`
);

const trackOpen = (name, dimensions) => {
  try {
    trackTrainingEvent(name, dimensions);
  } catch {
    // Disclosure interaction must remain available if analytics fails.
  }
};

const SectionHeading = ({ id, children }) => <h2 id={id}>{children}</h2>;

export function TrainingHero({ locale, offer, content, heroImage, heroAlt, onCta }) {
  const labels = ui[locale];
  const duration = formatDuration(offer.duration, locale);

  return (
    <section className="training-offer__hero training-story-block training-reveal" aria-labelledby="training-offer-title">
      <TrainingMedia src={heroImage} alt={heroAlt} placeholderLabel={labels.heroMedia} eager />
      <div className="training-story-block__copy">
        <p className="training-offer__eyebrow">{content.eyebrow}</p>
        <h1 id="training-offer-title">{content.headline}</h1>
        <p className="training-offer__support">{content.support}</p>
        <ul className="training-offer__facts">
          <li><strong>{labels.duration}</strong> {duration}</li>
          <li><strong>{labels.intake}</strong> {formatIntake(offer.intake, locale)}</li>
          <li>{labels.modes}</li>
          <li>
            {labels.capacity}{locale === 'fr' ? ' :' : ':'} {offer.capacity.inPerson} {labels.inPersonCapacity}, {' '}
            {offer.capacity.online} {labels.onlineCapacity}
          </li>
        </ul>
        <p className="training-offer__hero-total">{formatMoney(offer.total)}</p>
        <div className="training-offer__hero-actions">
          <button type="button" data-testid="training-cta-hero" onClick={() => onCta('hero')}>
            {content.primaryCta}
          </button>
          <a href="#programme">{content.secondaryCta}</a>
        </div>
      </div>
    </section>
  );
}

export function AudienceSection({ locale, content }) {
  const id = 'training-audience-title';
  return (
    <section className="training-section training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{ui[locale].audience}</SectionHeading>
      <ul className="training-card-list training-card-list--four">
        {content.map((situation) => <li key={situation} data-testid="audience-situation">{situation}</li>)}
      </ul>
    </section>
  );
}

export function TransformationSection({ locale, content, media }) {
  const id = 'training-transformation-title';
  return (
    <section className="training-section training-story-block training-reveal" aria-labelledby={id}>
      <TrainingMedia src={media?.transformation} alt={content.title} placeholderLabel={ui[locale].transformationMedia} />
      <div className="training-story-block__copy">
        <SectionHeading id={id}>{content.title}</SectionHeading>
        <div className="training-transformation__states">
          <p><strong>{ui[locale].before}</strong><span>{content.before}</span></p>
          <p><strong>{ui[locale].after}</strong><span>{content.after}</span></p>
        </div>
      </div>
    </section>
  );
}

export function CapabilitiesSection({ locale, content }) {
  const id = 'training-capabilities-title';
  return (
    <section className="training-section training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{ui[locale].capabilities}</SectionHeading>
      <ul className="training-card-list">
        {content.map((capability) => <li key={capability} data-testid="training-capability">{capability}</li>)}
      </ul>
    </section>
  );
}

export function ModulesSection({ locale, offer, content }) {
  const id = 'training-modules-title';
  return (
    <section id="programme" className="training-section training-accordion-section training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{ui[locale].modules}</SectionHeading>
      <Accordion.Root
        className="training-accordion"
        type="single"
        collapsible
        onValueChange={(moduleKey) => {
          if (moduleKey) trackOpen('module_open', { offerKey: offer.key, locale, moduleKey });
        }}
      >
        {content.map((module) => (
          <Accordion.Item className="training-accordion__item" key={module.key} value={module.key}>
            <Accordion.Header className="training-accordion__heading">
              <Accordion.Trigger className="training-accordion__trigger" data-testid="training-module-trigger">
                {module.title}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="training-accordion__content">
              <dl>
                <div><dt>{ui[locale].understand}</dt><dd>{module.understand}</dd></div>
                <div><dt>{ui[locale].practice}</dt><dd>{module.practice}</dd></div>
                <div><dt>{ui[locale].produce}</dt><dd>{module.produce}</dd></div>
              </dl>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </section>
  );
}

export function LearningMethodSection({ locale, content, media }) {
  const id = 'training-method-title';
  return (
    <section className="training-section training-story-block training-story-block--reverse training-reveal" aria-labelledby={id}>
      <TrainingMedia src={media?.method} alt={content.title} placeholderLabel={ui[locale].methodMedia} />
      <div className="training-story-block__copy">
        <SectionHeading id={id}>{content.title}</SectionHeading>
        <p>{content.introduction}</p>
        <ol className="training-workshop-thread">
          {content.steps.map((step) => <li key={step} data-testid="method-step">{step}</li>)}
        </ol>
        <p>{content.delivery}</p>
      </div>
    </section>
  );
}

export function LaboratorySection({ locale, content, media }) {
  const id = 'training-laboratory-title';
  return (
    <section className="training-section training-story-block training-reveal" aria-labelledby={id}>
      <TrainingMedia src={media?.laboratory} alt={content.title} placeholderLabel={ui[locale].laboratoryMedia} />
      <div className="training-story-block__copy">
        <SectionHeading id={id}>{content.title}</SectionHeading>
        <p>{content.introduction}</p>
        <ol className="training-workshop-thread training-workshop-thread--laboratory">
          {content.stages.map((stage) => <li key={stage} data-testid="laboratory-stage">{stage}</li>)}
        </ol>
        <p className="training-section__note">{content.note}</p>
      </div>
    </section>
  );
}

export function BonusesSection({ content }) {
  const id = 'training-bonuses-title';
  return (
    <section className="training-section training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{content.title}</SectionHeading>
      <ul className="training-card-list training-card-list--four">
        {content.items.map((item) => (
          <li key={item.title}><strong>{item.title}</strong><span>{item.description}</span></li>
        ))}
      </ul>
    </section>
  );
}

export function ProofSection({ locale, offer, content, media }) {
  const id = 'training-proof-title';
  const certifications = offer.certifications.map((certification) => (
    locale === 'en' && certification === 'Certification internationale'
      ? 'International certification'
      : certification
  ));
  const proofImages = media?.proof || [];
  return (
    <section className="training-section training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{content.title}</SectionHeading>
      <p>{content.introduction}</p>
      <div className="training-proof__placeholders">
        {content.placeholders.map((placeholder, index) => (
          <TrainingMedia
            key={placeholder}
            src={proofImages[index]}
            alt={placeholder}
            placeholderLabel={placeholder}
          />
        ))}
      </div>
      {certifications.length > 0 ? (
        <div className="training-proof__certification">
          <p><strong>{ui[locale].certifications}</strong></p>
          <ul>{certifications.map((label) => <li key={label}>{label}</li>)}</ul>
          {content.certification?.confirmationNote ? (
            <p>{content.certification.confirmationNote}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function InvestmentSection({ locale, offer, content, onCta }) {
  const sectionRef = useRef(null);
  const trackedRef = useRef(false);
  const id = 'training-investment-title';

  useEffect(() => {
    const observer = observeTrainingSection(sectionRef.current, () => {
      if (!trackedRef.current) {
        trackedRef.current = true;
        trackOpen('pricing_view', { offerKey: offer.key, locale });
      }
    });
    return () => observer?.disconnect();
  }, [locale, offer.key]);

  return (
    <section ref={sectionRef} className="training-section training-investment training-reveal" aria-labelledby={id}>
      <div className="training-investment__card">
        <SectionHeading id={id}>{content.title}</SectionHeading>
        <p>{content.introduction}</p>
        <div className="training-investment__arithmetic">
          <p>{feeText(content.trainingFeeLabel, offer.tuition, locale)}</p>
          <p>{feeText(content.registrationFeeLabel, offer.registration, locale)}</p>
          <p className="training-investment__total">{feeText(content.totalLabel, offer.total, locale)}</p>
        </div>
        <div className="training-investment__logistics">
          <p><strong>{content.logisticsLabel}</strong></p>
          <p>{formatDuration(offer.duration, locale)} · {formatIntake(offer.intake, locale)} · {ui[locale].modes}</p>
          <p><strong>{ui[locale].included}</strong></p>
          <ul>{content.included.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div className="training-investment__action">
          <p>{content.helper}</p>
          <button type="button" data-testid="training-cta-investment" onClick={() => onCta('investment')}>
            {ui[locale].investmentCta}
          </button>
        </div>
      </div>
    </section>
  );
}

export function FaqSection({ locale, offer, content }) {
  const id = 'training-faq-title';
  return (
    <section className="training-section training-accordion-section training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{ui[locale].faq}</SectionHeading>
      <Accordion.Root
        className="training-accordion"
        type="single"
        collapsible
        onValueChange={(faqKey) => {
          if (faqKey) trackOpen('faq_open', { offerKey: offer.key, locale, faqKey });
        }}
      >
        {content.map((item) => (
          <Accordion.Item className="training-accordion__item" key={item.key} value={item.key}>
            <Accordion.Header className="training-accordion__heading">
              <Accordion.Trigger className="training-accordion__trigger" data-testid="training-faq-trigger">
                {item.question}
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="training-accordion__content"><p>{item.answer}</p></Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </section>
  );
}

export function FinalCtaSection({ content, onCta }) {
  const id = 'training-final-title';
  return (
    <section className="training-section training-final training-reveal" aria-labelledby={id}>
      <SectionHeading id={id}>{content.headline}</SectionHeading>
      <p>{content.support}</p>
      <p><strong>{content.summary}</strong></p>
      <button type="button" data-testid="training-cta-final" onClick={() => onCta('final')}>
        {content.cta}
      </button>
    </section>
  );
}
